// ─── NFL DATA TRANSFORMERS — pure functions, no React dependencies ────────────
// ESPN `summary?event=` payload → app-ready shapes. Single job: one row per athlete
// PRESENT in the box score (any stat group) with the passing/rushing/receiving line
// populated only from the athlete's own group, plus one-stat resolution for a leg.
//
// Shape verified on the real Week-1 fixture docs/fixtures/nfl-summary-401872661.json
// (CHI @ CAR, 2026-09-13, FINAL):
//   boxscore.players[team].statistics[group].athletes[i] = { athlete: {id: "4431611",
//   displayName}, stats: ["21/29", "269", ...] } with stats[] POSITIONAL STRINGS aligned
//   to statistics[group].keys[]. Composites: "completions/passingAttempts" -> "21/29",
//   "sacks-sackYardsLost" -> "2-8". The key `interceptions` exists in TWO groups (passing =
//   thrown, defensive `interceptions` = caught) -- fields are mapped by (group, key), never
//   by key alone. Team `totals` disagree with athlete lines (ESPN nets sacks) -- never read.
//   athlete.id is a STRING; ~10% of names carry Jr./Sr./II/III.
//
// D4 (ratified 2026-09-14) + G1 (#1040 PR C, GPT round-1 F1): a field whose group the
// athlete is not in stays undefined (the writer persists null = "inapplicable", like
// NHL goalie columns on a skater). getPlayerStat is THREE-WAY: athlete absent from the
// entire box score -> null; present but NOT in the stat's group -> 0 (a RB with no
// targets has 0 receptions); IN the group but the required field is undefined (ESPN
// "--", "", null, non-integer) -> null, never 0 -- a malformed field must be a
// pending leg, not a frozen zero. nfl:anytime_td (#1353 PR 2) is NOT a box-score field: it is
// the ESPN core plays `scorer` count, reconciled against the box score under the spec §6
// C1-C9 contract (reconcileTouchdowns below), merged onto rows as `touchdowns`, and read by
// roster id through getPlayerStat's 5th argument (ctx). Passing TDs never count.
// #1366 / #1382: with a prop ctx (propReads) the four O/U markets are read by roster id
// (explainPropRead), and a not-in-group 0 on a freeze tick needs boxTeamIdentityOk.
import { findPlayerMatch, normalizeStr } from "./betting";
import { resolvePropRosterId } from "./roster-identity";

const OFFENSIVE_GROUPS = ['passing', 'rushing', 'receiving'];
// Every group ESPN publishes in an NFL box score (Week-1 fixture). `groups` on a parsed row
// records which of THESE the athlete appeared in; a payload-controlled name outside the
// list is never recorded (it is persisted verbatim into live_stats.players otherwise).
const KNOWN_GROUPS = new Set([
  ...OFFENSIVE_GROUPS, 'fumbles', 'defensive', 'interceptions', 'kickReturns', 'puntReturns', 'kicking', 'punting',
]);

// (group, key) -> parsed field. Composite keys are split by the separator and mapped to
// two fields. Anything not listed is ignored on purpose (defense/kicking/returns deferred).
const FIELD_MAP = {
  passing: {
    'completions/passingAttempts': ['completions', 'passingAttempts'],
    passingYards: 'passingYards',
    passingTouchdowns: 'passingTouchdowns',
    interceptions: 'interceptionsThrown',
  },
  rushing: {
    rushingAttempts: 'rushingAttempts',
    rushingYards: 'rushingYards',
    rushingTouchdowns: 'rushingTouchdowns',
  },
  receiving: {
    receptions: 'receptions',
    receivingTargets: 'receivingTargets',
    receivingYards: 'receivingYards',
    receivingTouchdowns: 'receivingTouchdowns',
  },
};

const abbrevOrNull = (v) => (typeof v === 'string' && v.length >= 1 && v.length <= 4 ? v : null);

// Roster-sync's id guard (nfl-roster-sync.js): a positive int4 string, else the athlete is
// dropped -- never a fabricated id reaching the player_id bigint column.
function athleteId(raw) {
  const s = String(raw ?? '').trim();
  if (!/^[1-9]\d*$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n <= 2147483647 ? n : null;
}

// ── #1353 PR 2: strict identity for nfl:anytime_td (spec §3 nfl.js row, r5.2 / r5.4 / r5.5) ──
// A fuzzy initial+surname match (betting.js matchPlayer) can hand an absent player's leg a
// present namesake's TDs (GPT G2), so anytime TD resolves a NAME only to a rosters player_id,
// and reads counts BY ID. Key: normalizeStr (trim, lowercase, diacritics), hyphens -> spaces,
// drop . ' ', collapse whitespace. Generational suffix handling mirrors betting.js:57-71
// (strip ONE trailing jr|sr|ii|iii|iv|v from a 3+-token name; G9(a): both suffixed and
// different -> different people). Kept local: betting.js is outside this PR's file set.
const STRICT_SUFFIX = /^(jr|sr|ii|iii|iv|v)$/;
function strictParts(name) {
  if (typeof name !== 'string') return null;
  const key = normalizeStr(name).replace(/-/g, ' ').replace(/[.''’]/g, '').replace(/\s+/g, ' ').trim();
  return key ? key.split(' ') : null;
}
function splitSuffix(parts) {
  if (parts.length >= 3 && STRICT_SUFFIX.test(parts[parts.length - 1])) {
    return { base: parts.slice(0, -1).join(' '), suffix: parts[parts.length - 1] };
  }
  return { base: parts.join(' '), suffix: null };
}
export function strictNameMatch(a, b) {
  const pa = strictParts(a);
  const pb = strictParts(b);
  if (!pa || !pb) return false;
  const A = splitSuffix(pa);
  const B = splitSuffix(pb);
  if (A.suffix && B.suffix && A.suffix !== B.suffix) return false; // G9(a)
  return A.base === B.base;
}

// rosters rows (league=nfl, the game's two teams, any active / roster_status) -> the ONE
// distinct player_id whose name strictly matches, else null (0 or >1 ids, or no roster).
// A traded player facing his former team has two rows but one id: resolves (r5.5).
export function resolveRosterPlayerId(roster, name) {
  if (!Array.isArray(roster)) return null;
  const ids = new Set();
  for (const r of roster) {
    if (!strictNameMatch(name, r?.player_name)) continue;
    const id = athleteId(r?.player_id);
    if (id == null) return null; // a matching row with an unusable id could hide a namesake: fail closed
    ids.add(id);
  }
  return ids.size === 1 ? ids.values().next().value : null;
}

// #1366 (spec §6 step 2): the strict matches of a name as a COUNT -- resolveRosterPlayerId returns
// null for both "none" and "more than one", and the prop resolver must tell them apart. Same
// matcher and id guard as resolveRosterPlayerId, whose contract is unchanged.
export function strictRosterMatches(roster, name) {
  const ids = new Set();
  let unusable = false;
  if (!Array.isArray(roster)) return { ids, unusable };
  for (const r of roster) {
    if (!strictNameMatch(name, r?.player_name)) continue;
    const id = athleteId(r?.player_id);
    if (id == null) { unusable = true; continue; }
    ids.add(id);
  }
  return { ids, unusable };
}

// "269" -> 269, "-4" -> -4; anything else ("--", "", "12.5", "1e3", "0x1F", out of int4
// range) -> undefined (write null). Never 0 for a missing value: Number("") is 0. The 12
// target columns are `integer`, and one non-integer value 400s the whole game's atomic
// upsert (pre-Codex Fail-Closed + Security P2).
const INT_RE = /^-?\d+$/;
function num(raw) {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!INT_RE.test(s)) return undefined;
  const n = Number(s);
  return Number.isSafeInteger(n) && Math.abs(n) <= 2147483647 ? n : undefined;
}

// Group and key names are payload strings: own-property lookups only, so `__proto__` /
// `constructor` / `hasOwnProperty` never resolve through Object.prototype.
const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

function applyStat(row, group, key, raw) {
  if (typeof key !== 'string' || !hasOwn(FIELD_MAP, group) || !hasOwn(FIELD_MAP[group], key)) return;
  const target = FIELD_MAP[group][key];
  if (Array.isArray(target)) {
    const sep = key.includes('/') ? '/' : '-';
    const parts = String(raw ?? '').split(sep);
    const a = num(parts[0]); const b = num(parts[1]);
    if (a !== undefined) row[target[0]] = a;
    if (b !== undefined) row[target[1]] = b;
    return;
  }
  const v = num(raw);
  if (v !== undefined) row[target] = v;
}

export function parseBoxscorePlayers(data) {
  const teams = data?.boxscore?.players;
  if (!Array.isArray(teams)) return [];
  const rows = new Map(); // id -> row
  for (const team of teams) {
    const abbrev = abbrevOrNull(team?.team?.abbreviation);
    const groups = Array.isArray(team?.statistics) ? team.statistics : [];
    for (const group of groups) {
      const gname = typeof group?.name === 'string' ? group.name : null;
      const keys = Array.isArray(group?.keys) ? group.keys : [];
      const athletes = Array.isArray(group?.athletes) ? group.athletes : [];
      if (!gname) continue;
      for (const entry of athletes) {
        const id = athleteId(entry?.athlete?.id);
        const name = entry?.athlete?.displayName;
        if (id == null || typeof name !== 'string' || !name) continue;
        let row = rows.get(id);
        if (!row) {
          row = { id, name, team: abbrev, groups: [] };
          rows.set(id, row);
        }
        if (KNOWN_GROUPS.has(gname) && !row.groups.includes(gname)) row.groups.push(gname);
        const stats = Array.isArray(entry?.stats) ? entry.stats : [];
        keys.forEach((key, i) => applyStat(row, gname, key, stats[i]));
      }
    }
  }
  return Array.from(rows.values());
}

// (market -> the ESPN group it must come from, and the parsed field). Anything not
// listed resolves null: nfl:anytime_td (read by id from ctx, below), nfl:first_td / nfl:last_td (need scoring-
// play order, not a box score), team markets, unknown ids.
const GROUP_FOR = {
  'nfl:passing_yards': 'passing',
  'nfl:rushing_yards': 'rushing',
  'nfl:receiving_yards': 'receiving',
  'nfl:receptions': 'receiving',
};
const FIELD_FOR = {
  'nfl:passing_yards': 'passingYards',
  'nfl:rushing_yards': 'rushingYards',
  'nfl:receiving_yards': 'receivingYards',
  'nfl:receptions': 'receptions',
};

// #1366 / #1382 (spec §6): the O/U markets read by roster id. Drift-tested equal to the keys of
// NFL_CORE_STAT_FOR; nfl:anytime_td keeps its own strict read (anytimeTd below).
export const NFL_PROP_READ_MARKETS = Object.freeze(['nfl:passing_yards', 'nfl:rushing_yards', 'nfl:receiving_yards', 'nfl:receptions']);
const PROP_READ_SET = new Set(NFL_PROP_READ_MARKETS);

// #1353 PR 3b (r5b.2 G1): the ESPN core game-statistics field for each admitted NFL player-prop
// market except nfl:anytime_td (its played-zero rule is the §6 three-gate rule, read from the
// reconciled TD state). Drift-tested against GRADABLE_MARKETS_BY_LEAGUE.nfl.
export const NFL_CORE_STAT_FOR = Object.freeze({
  'nfl:passing_yards': Object.freeze({ category: 'passing', name: 'passingYards' }),
  'nfl:rushing_yards': Object.freeze({ category: 'rushing', name: 'rushingYards' }),
  'nfl:receiving_yards': Object.freeze({ category: 'receiving', name: 'receivingYards' }),
  'nfl:receptions': Object.freeze({ category: 'receiving', name: 'receptions' }),
});

// One market's value from a core statistics `splits.categories` array: a finite number, else null.
// ESPN sends floats (0.0); === 0 is the played-zero evidence.
export function coreStatValue(categories, market) {
  if (!Array.isArray(categories) || !hasOwn(NFL_CORE_STAT_FOR, market)) return null;
  const { category, name } = NFL_CORE_STAT_FOR[market];
  const cat = categories.find((c) => c?.name === category);
  const st = Array.isArray(cat?.stats) ? cat.stats.find((s) => s?.name === name) : null;
  const v = st?.value;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// The raw box-score athlete-id set (the set §6 C4 uses), or null when the payload is not two
// teams or any athlete id is unparseable (participation then reads every leg as unknown).
export function boxAthleteIds(summary) {
  const teams = summary?.boxscore?.players;
  if (!Array.isArray(teams) || teams.length !== 2) return null;
  const ids = new Set();
  for (const tp of teams) {
    for (const g of Array.isArray(tp?.statistics) ? tp.statistics : []) {
      for (const a of Array.isArray(g?.athletes) ? g.athletes : []) {
        const id = athleteId(a?.athlete?.id);
        if (id == null) return null;
        ids.add(id);
      }
    }
  }
  return ids;
}

export const parseAthleteId = athleteId;

// [r2 P1-1, D11] Played-zero cross-check: the candidate's team must pass a box-score identity for
// the leg's market, from the SAME summary. A dropped athlete row breaks it (Kelce: KC receiving 210
// vs passing 225); a real zero keeps it. Any unparseable value or missing column -> false (closed).
//   receiving/passing yards: Σ passing passingYards === Σ receiving receivingYards
//   receptions:              Σ passing completions ("C/A" made part) === Σ receiving receptions
//   rushing yards:           Σ rushing rushingYards === boxscore.teams[team] rushingYards
const IDENTITY_FOR = {
  'nfl:receiving_yards': 'passRecvYards', 'nfl:passing_yards': 'passRecvYards',
  'nfl:receptions': 'completionsReceptions', 'nfl:rushing_yards': 'rushingTeam',
};
function columnSum(tp, group, key, madePart = false) {
  const g = (Array.isArray(tp?.statistics) ? tp.statistics : []).find((x) => x?.name === group);
  const i = Array.isArray(g?.keys) ? g.keys.indexOf(key) : -1;
  if (i < 0 || !Array.isArray(g?.athletes)) return null;
  let sum = 0;
  for (const a of g.athletes) {
    const v = madePart ? madeOf(a?.stats?.[i]) : num(a?.stats?.[i]);
    if (v === undefined) return null;
    sum += v;
  }
  return sum;
}
export function boxTeamIdentityOk(summary, espnTeamId, market) {
  const kind = hasOwn(IDENTITY_FOR, market) ? IDENTITY_FOR[market] : null;
  const tp = (Array.isArray(summary?.boxscore?.players) ? summary.boxscore.players : []).find((x) => athleteId(x?.team?.id) === espnTeamId);
  if (!kind || !tp) return false;
  if (kind === 'passRecvYards') {
    const p = columnSum(tp, 'passing', 'passingYards'); const r = columnSum(tp, 'receiving', 'receivingYards');
    return p !== null && r !== null && p === r;
  }
  if (kind === 'completionsReceptions') {
    const c = columnSum(tp, 'passing', 'completions/passingAttempts', true); const r = columnSum(tp, 'receiving', 'receptions');
    return c !== null && r !== null && c === r;
  }
  const rows = columnSum(tp, 'rushing', 'rushingYards');
  const team = (Array.isArray(summary?.boxscore?.teams) ? summary.boxscore.teams : []).find((x) => athleteId(x?.team?.id) === espnTeamId);
  const st = (Array.isArray(team?.statistics) ? team.statistics : []).find((x) => x?.name === 'rushingYards');
  const total = num(st?.displayValue);
  return rows !== null && total !== undefined && rows === total;
}

// #1382 (spec §6): the ESPN team id of the ONE box-score team block holding the athlete; zero or
// two blocks -> null (the caller then fails closed, R8).
export function boxTeamIdOf(summary, id) {
  const teams = Array.isArray(summary?.boxscore?.players) ? summary.boxscore.players : [];
  const hits = teams.filter((tp) => (Array.isArray(tp?.statistics) ? tp.statistics : [])
    .some((g) => (Array.isArray(g?.athletes) ? g.athletes : []).some((a) => athleteId(a?.athlete?.id) === id)));
  return hits.length === 1 ? athleteId(hits[0]?.team?.id) : null;
}

// #1366 / #1382 (spec §6 R0-R8): an O/U prop's value read by the leg's resolved roster id, or the
// reason it must wait. R0 (no prop ctx, or not an O/U market) is today's getPlayerStat, untouched.
export function explainPropRead(players, fullName, statType, ctx) {
  if (ctx?.propReads !== true || !PROP_READ_SET.has(statType)) return { value: getPlayerStat(players, fullName, statType, null, ctx) };
  const r = resolvePropRosterId(ctx.roster, fullName, { strictMatches: strictRosterMatches, parseId: athleteId });
  if (r.wait) return { wait: r.wait };
  const row = Array.isArray(players) ? players.find((p) => p?.id === r.id) : undefined;
  if (!row) return { value: null };                                   // R3: absent -> participation decides
  if (!Array.isArray(row.groups)) return { value: null };             // R4
  if (row.groups.includes(GROUP_FOR[statType])) return { value: row[FIELD_FOR[statType]] ?? null }; // R5
  if (ctx.freeze !== true) return { value: 0 };                       // R6: live, no gate
  const teamId = boxTeamIdOf(ctx.summary, r.id);
  return teamId != null && boxTeamIdentityOk(ctx.summary, teamId, statType) ? { value: 0 } : { wait: 'team_total_mismatch' }; // R7 / R8
}

// G1 three-way rule -- see the header. Absent -> null; present, not in the group -> 0;
// in the group, field undefined -> null. A row with no `groups` array cannot say which
// case it is, so it fails closed (null): every row this repo writes carries `groups`.
// #1353 PR 2: nfl:anytime_td reads ONLY ctx (spec §3 r5.4/r5.6): the leg name resolves to one
// rosters player_id, and the count is that id's box row `touchdowns`, present only on a tick
// whose TD state is `ok`. No ctx (client, recovery stat_value backfill) -> null by design.
export function getPlayerStat(players, fullName, statType, legPlayerId, ctx) {
  if (statType === 'nfl:anytime_td') return anytimeTd(players, fullName, ctx);
  // #1366 / #1382: a prop ctx reads the O/U markets by roster id (a wait reads as null here).
  if (ctx?.propReads === true && PROP_READ_SET.has(statType)) return explainPropRead(players, fullName, statType, ctx).value ?? null;
  const match = findPlayerMatch(players, fullName, legPlayerId);
  if (!match) return null;
  if (typeof statType !== 'string' || !Object.prototype.hasOwnProperty.call(GROUP_FOR, statType)) return null;
  if (!Array.isArray(match.groups)) return null;
  if (!match.groups.includes(GROUP_FOR[statType])) return 0;
  return match[FIELD_FOR[statType]] ?? null;
}

function anytimeTd(players, fullName, ctx) {
  if (!Array.isArray(players) || ctx?.tdState !== 'ok' || !Array.isArray(ctx?.roster)) return null;
  const id = resolveRosterPlayerId(ctx.roster, fullName);
  if (id == null) return null;
  const row = players.find((p) => p?.id === id);
  const v = row?.touchdowns;
  return Number.isInteger(v) && v >= 0 ? v : null;
}

// ── #1353 PR 2: TD scorer reconciliation (spec §6, r5 completeness contract) ─────────────
// plays = ESPN core plays feed (limit=400); summary = the ESPN summary the adapter polls.
// Returns { scorers: Map<athleteId, tdCount> | null, failedCheck }. ANY failed check -> null
// for the WHOLE game (no partial map). C1-C6 run on every tick; C7-C9 + the XP guard on freeze
// ticks only. Evidence: docs/audits/2026-10-05-1353-r5-td-feed-reconciliation.md (342/349 pass).
// NOT evidence (measured): summary scoringPlays (same data as core plays), item running scores.
const TD_FIELD = {
  rushing: 'rushingTouchdowns', receiving: 'receivingTouchdowns', defensive: 'defensiveTouchdowns',
  interceptions: 'interceptionTouchdowns', kickReturns: 'kickReturnTouchdowns', puntReturns: 'puntReturnTouchdowns',
};
const refNum = (ref, kind) => {
  const m = typeof ref === 'string' ? new RegExp(`/${kind}/(\\d+)(?:[/?]|$)`).exec(ref) : null;
  return m ? athleteId(m[1]) : null;
};
const fail = (failedCheck) => ({ scorers: null, failedCheck });
// "12/14" -> 12 (made), else undefined.
const madeOf = (raw) => num(String(raw ?? '').split('/')[0]);

function readBox(summary) {
  const teams = summary?.boxscore?.players;
  if (!Array.isArray(teams) || teams.length !== 2) return { fail: 'C5' };
  const teamOf = new Map(); const parts = new Map();
  const xp = new Map(); const fg = new Map(); let kickBad = false;
  for (const tp of teams) {
    const tid = athleteId(tp?.team?.id);
    if (tid == null || xp.has(tid)) return { fail: 'C5' };
    xp.set(tid, 0); fg.set(tid, 0);
    for (const g of Array.isArray(tp?.statistics) ? tp.statistics : []) {
      const gname = typeof g?.name === 'string' ? g.name : null;
      const keys = Array.isArray(g?.keys) ? g.keys : [];
      for (const a of Array.isArray(g?.athletes) ? g.athletes : []) {
        const id = athleteId(a?.athlete?.id);
        if (id == null) return { fail: 'C4' };
        const prev = teamOf.get(id);
        if (prev != null && prev !== tid) return { fail: 'C4' };
        teamOf.set(id, tid);
        const stats = Array.isArray(a?.stats) ? a.stats : [];
        if (gname && hasOwn(TD_FIELD, gname)) {
          const i = keys.indexOf(TD_FIELD[gname]);
          if (i >= 0) {
            const v = num(stats[i]);
            if (v === undefined || v < 0) return { fail: 'C6' };
            const p = parts.get(id) || { rushing: 0, receiving: 0, defensive: 0, interceptions: 0, kickReturns: 0, puntReturns: 0 };
            p[gname] += v; parts.set(id, p);
          }
        }
        if (gname === 'kicking') {
          const f = madeOf(stats[keys.indexOf('fieldGoalsMade/fieldGoalAttempts')]);
          const x = madeOf(stats[keys.indexOf('extraPointsMade/extraPointAttempts')]);
          if (f === undefined || x === undefined || f < 0 || x < 0) kickBad = true;
          else { fg.set(tid, fg.get(tid) + f); xp.set(tid, xp.get(tid) + x); }
        }
      }
    }
  }
  const boxTd = new Map();
  for (const [id, p] of parts) {
    boxTd.set(id, p.rushing + p.receiving + Math.max(p.defensive, p.interceptions + p.kickReturns + p.puntReturns));
  }
  return { teamOf, boxTd, xp, fg, kickBad };
}

// Two-point phrasing, ported 1:1 from the evidence script (r5.1 addendum). The try belongs to
// the TD item; a separate No-Play penalty item is never a scoring TD item, so never counted.
function ownTwoPoint(text) {
  const T = String(text || '').toUpperCase();
  const beforeDef = T.split('DEFENSIVE TWO-POINT')[0];
  return (T.includes('TWO-POINT CONVERSION ATTEMPT') && beforeDef.includes('ATTEMPT SUCCEEDS'))
    || /\([^)]*FOR TWO-POINT CONVERSION\)/.test(T);
}
function defensiveTwoPoint(text) {
  const segs = String(text || '').toUpperCase().split('DEFENSIVE TWO-POINT ATTEMPT');
  return segs.length > 1 && segs[1].includes('ATTEMPT SUCCEEDS');
}

export function reconcileTouchdowns(plays, summary, { freeze = false, isFinal = false } = {}) {
  // C1 feed shape
  const items = Array.isArray(plays?.items) ? plays.items : null;
  if (!items) return fail('C1');
  // The whole page envelope is pinned to the two shapes measured over the 349-game corpus (Codex
  // P1 rounds 1-2 on #1369): empty = pageIndex 0 / pageCount 0 / count 0 / no items (401831718,
  // accepted on live ticks only); non-empty = page 1 of 1 with count === items.length (x348).
  const emptyLive = !freeze && plays.count === 0 && plays.pageCount === 0 && plays.pageIndex === 0 && items.length === 0;
  if (!emptyLive && !(plays.pageIndex === 1 && plays.pageCount === 1 && plays.count === items.length && items.length > 0)) return fail('C1');
  // C2 classify: every scoring item names its scoringType
  const scoring = items.filter((it) => it?.scoringPlay === true);
  if (scoring.some((it) => typeof it?.scoringType?.name !== 'string' || !it.scoringType.name)) return fail('C2');
  const box = readBox(summary);
  if (box.fail) return fail(box.fail);
  // C3-C5 attribution, per TD item in feed order
  const core = new Map();
  const tdItems = scoring.filter((it) => it.scoringType.name === 'touchdown');
  for (const it of tdItems) {
    const sc = Array.isArray(it.participants) ? it.participants.filter((x) => x?.type === 'scorer') : [];
    if (sc.length !== 1) return fail('C3');
    const sid = refNum(sc[0]?.athlete?.$ref, 'athletes');
    if (sid == null || !box.teamOf.has(sid)) return fail('C4');
    const playTeam = refNum(it?.team?.$ref, 'teams');
    if (playTeam == null || box.teamOf.get(sid) !== playTeam) return fail('C5');
    core.set(sid, (core.get(sid) || 0) + 1);
  }
  // C6 per-athlete agreement
  const ids = new Set([...core.keys(), ...[...box.boxTd].filter(([, v]) => v > 0).map(([k]) => k)]);
  for (const id of ids) if ((core.get(id) || 0) !== (box.boxTd.get(id) || 0)) return fail('C6');
  if (!freeze) return { scorers: core, failedCheck: null };
  // C7 finality: the feed has the last word and it equals the header (Ultron P1-2, r5.1 G3)
  const comps = summary?.header?.competitions?.[0]?.competitors;
  const side = (ha) => (Array.isArray(comps) ? comps.find((c) => c?.homeAway === ha) : null);
  const home = side('home'); const away = side('away');
  const homeScore = num(home?.score); const awayScore = num(away?.score);
  const last = items[items.length - 1];
  if (isFinal !== true || last?.type?.text !== 'End of Game'
    || homeScore === undefined || awayScore === undefined || homeScore < 0 || awayScore < 0
    || !Number.isInteger(last.homeScore) || !Number.isInteger(last.awayScore)
    || last.homeScore !== homeScore || last.awayScore !== awayScore) return fail('C7');
  // C8 final-score identity, per team
  const homeId = athleteId(home?.team?.id); const awayId = athleteId(away?.team?.id);
  if (homeId == null || awayId == null || !box.xp.has(homeId) || !box.xp.has(awayId) || box.kickBad) return fail('C8');
  const other = new Map([[homeId, awayId], [awayId, homeId]]);
  const td = new Map([[homeId, 0], [awayId, 0]]); const two = new Map([[homeId, 0], [awayId, 0]]);
  const saf = new Map([[homeId, 0], [awayId, 0]]); const fgPlays = new Map([[homeId, 0], [awayId, 0]]);
  for (const it of scoring) {
    const t = refNum(it?.team?.$ref, 'teams');
    if (!td.has(t)) return fail('C8');
    const st = it.scoringType.name;
    if (st === 'touchdown') {
      td.set(t, td.get(t) + 1);
      if (ownTwoPoint(it.text)) two.set(t, two.get(t) + 1);
      if (defensiveTwoPoint(it.text)) two.set(other.get(t), two.get(other.get(t)) + 1);
    } else if (st === 'field-goal') fgPlays.set(t, fgPlays.get(t) + 1);
    else if (st === 'safety') saf.set(t, saf.get(t) + 1);
    else if (st === 'two-point-conversion') two.set(t, two.get(t) + 1);
    else return fail('C8');
  }
  const header = new Map([[homeId, homeScore], [awayId, awayScore]]);
  for (const t of [homeId, awayId]) {
    if (header.get(t) !== 6 * td.get(t) + box.xp.get(t) + 3 * box.fg.get(t) + 2 * (saf.get(t) + two.get(t))) return fail('C8');
  }
  // C9 FG pinning (GPT r5.5 blocker 1)
  for (const t of [homeId, awayId]) if (box.fg.get(t) !== fgPlays.get(t)) return fail('C9');
  // XP guard (r5.7): XP made <= TD items
  for (const t of [homeId, awayId]) if (box.xp.get(t) > td.get(t)) return fail('XP');
  return { scorers: core, failedCheck: null };
}

// Merge a complete scorer map into parsed box rows: touchdowns on EVERY row (0 = in the box,
// not a scorer, all checks passed). A null map leaves the rows untouched (no key = stat null).
export function mergeTouchdowns(rows, scorers) {
  if (!(scorers instanceof Map) || !Array.isArray(rows)) return rows;
  for (const r of rows) r.touchdowns = scorers.get(r?.id) ?? 0;
  return rows;
}

export { OFFENSIVE_GROUPS };
